import { Textarea } from "@/components/ui/textarea";
import { CircleAlert, GripHorizontal, Info, LoaderCircle, Shield, ShieldAlert, TriangleAlert } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ConcernCard } from "./ConcernCard";
import { type DockTone, dockStatus, newHighKeys, sortConcerns } from "./auditDockModel";
import { type AuditBarTrigger, useAuditBarEvents } from "./useAuditBarEvents";
import type { BackgroundAuditState } from "./useBackgroundCodeRuleAudit";

const PEEK_HEIGHT = 160;

const TONE_STYLES: Record<DockTone, string> = {
  high: "bg-red-100 text-red-900 border-red-300",
  medium: "bg-amber-100 text-amber-900 border-amber-300",
  clear: "bg-slate-100 text-slate-700 border-slate-200",
  idle: "bg-gray-50 text-gray-600 border-gray-200",
  checking: "bg-gray-50 text-gray-600 border-gray-200",
  failed: "bg-slate-100 text-slate-700 border-slate-200",
};

const TONE_ICONS: Record<DockTone, typeof Info> = {
  high: ShieldAlert,
  medium: TriangleAlert,
  clear: Info,
  idle: Shield,
  checking: LoaderCircle,
  failed: CircleAlert,
};

function AuditPane({ audit }: { audit: BackgroundAuditState }) {
  const { busy, highCount, mediumCount } = dockStatus(audit);
  const { phase, result } = audit;
  const hasFindings = result !== null && highCount + mediumCount > 0;

  const statusText = (() => {
    if (phase === "idle") return "The audit runs a moment after you change a code.";
    if (phase === "failed") return "Codes unchecked. The rule check could not run. You can still finalise.";
    if (result === null) return "Checking your codes…";
    if (busy) return "Updating…";
    if (!hasFindings) return "No concerns found in the last check. It can miss things, so use your own judgement.";
    return null;
  })();

  return (
    <div className="flex-1 min-w-0 overflow-y-auto border-l border-gray-300 pl-4 space-y-2">
      <p aria-live="polite" className="text-sm text-gray-600">
        {statusText}
      </p>
      {hasFindings && phase !== "failed" && (
        <ul className={`space-y-2 ${busy ? "opacity-60" : ""}`}>
          {sortConcerns(result.concerns).map((concern, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: concerns have no id and the list is replaced as a whole
            <ConcernCard key={index} concern={concern} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface ReviewNotesProps {
  caseId: string;
  initialNotes?: string;
  onSave: (notes: string) => Promise<void>;
  audit?: BackgroundAuditState;
}

type AuditButtonProps = {
  audit: BackgroundAuditState;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: () => void;
  onDoubleClick: () => void;
};

function AuditButton({ audit, onMouseDown, onClick, onDoubleClick }: AuditButtonProps) {
  const { tone, label, busy } = dockStatus(audit);
  const Icon = TONE_ICONS[tone];
  const spinsAsIcon = tone === "checking";
  return (
    <button
      type="button"
      data-tone={tone}
      aria-label={`Audit status: ${label}. Open the audit findings`}
      className={`flex flex-1 items-center justify-center gap-2 border-l text-sm cursor-ns-resize ${TONE_STYLES[tone]}`}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <Icon className={`h-4 w-4 ${spinsAsIcon ? "animate-spin" : ""}`} aria-hidden="true" />
      <span>{label}</span>
      {busy && !spinsAsIcon && <LoaderCircle className="h-3 w-3 animate-spin" aria-hidden="true" />}
    </button>
  );
}

const ReviewNotesComponent = ({ caseId, initialNotes = "", onSave, audit }: ReviewNotesProps) => {
  // Store onSave in ref to access latest function without causing re-renders
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);
  const [notes, setNotes] = useState(initialNotes);
  const [height, setHeight] = useState(40); // Default collapsed height (just drag handle)
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastDragY = useRef(0);
  const lastDragHeight = useRef(40);
  const barEvents = useAuditBarEvents(caseId, audit);
  const didDragRef = useRef(false);
  const previousCaseIdRef = useRef<string>(caseId);
  const [isPeek, setIsPeek] = useState(false);
  const peekedKeysRef = useRef<Set<string>>(new Set());
  const previousPhaseRef = useRef(audit?.phase);
  const heightRef = useRef(height);
  heightRef.current = height;
  const peekCaseIdRef = useRef(caseId);

  // Load saved height preference on mount (only in browser environment)
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const savedHeight = localStorage.getItem("review-notes-height");
      if (savedHeight) {
        const parsedHeight = parseInt(savedHeight, 10);
        if (parsedHeight >= 40 && parsedHeight <= 600) {
          setHeight(parsedHeight);
          setIsExpanded(parsedHeight > 40);
          if (parsedHeight > 40) barEvents.markOpen();
        }
      }
    }
  }, [barEvents]);

  // Save height preference when it changes (only in browser environment)
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage && height !== 40 && !isPeek) {
      localStorage.setItem("review-notes-height", height.toString());
    }
  }, [height, isPeek]);

  const auditPhase = audit?.phase;
  const auditResult = audit?.result;
  useEffect(() => {
    const previousPhase = previousPhaseRef.current;
    previousPhaseRef.current = auditPhase;
    if (previousPhase !== "checking" || auditPhase !== "ready" || !auditResult) return;

    const freshKeys = newHighKeys(auditResult.concerns, peekedKeysRef.current);
    for (const key of freshKeys) peekedKeysRef.current.add(key);
    if (freshKeys.length > 0 && heightRef.current === 40) {
      setHeight(PEEK_HEIGHT);
      setIsExpanded(true);
      setIsPeek(true);
      barEvents.peekShown(freshKeys.length);
    }
  }, [auditPhase, auditResult, barEvents]);

  useEffect(() => {
    if (peekCaseIdRef.current === caseId) return;
    peekCaseIdRef.current = caseId;
    peekedKeysRef.current = new Set();
    if (isPeek) {
      setHeight(40);
      setIsExpanded(false);
      setIsPeek(false);
      barEvents.restartIfOpen(false);
      return;
    }
    barEvents.restartIfOpen(heightRef.current > 40);
  }, [caseId, isPeek, barEvents]);

  // Update notes only when case changes (not when initialNotes updates for same case)
  useEffect(() => {
    if (previousCaseIdRef.current !== caseId) {
      setNotes(initialNotes);
      previousCaseIdRef.current = caseId;
    }
  }, [caseId, initialNotes]); // Sync when navigating to a new case

  // Auto-save with debounce - read directly from textarea to ensure we save what's actually in the input
  useEffect(() => {
    const currentValue = textareaRef.current?.value ?? notes;
    if (currentValue === initialNotes) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save (1 second debounce)
    saveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        // Read directly from textarea ref to get the latest value
        const valueToSave = textareaRef.current?.value ?? notes;
        // Use ref to access latest onSave function
        await onSaveRef.current(valueToSave);
      } catch (error) {
        console.error("Failed to save notes:", error);
        toast.error("Failed to save notes. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [notes, initialNotes, caseId]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      setIsDragActive(true);
      dragStartY.current = e.clientY;
      dragStartHeight.current = height;
      lastDragY.current = e.clientY;
      lastDragHeight.current = height;
      didDragRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
    [height],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaY = dragStartY.current - e.clientY;
      const maxHeight = Math.min(600, window.innerHeight - 80);
      const newHeight = Math.max(40, Math.min(maxHeight, dragStartHeight.current + deltaY));

      // Only update if the change is significant enough to avoid jitter
      if (Math.abs(e.clientY - lastDragY.current) > 2) {
        setIsPeek(false);
        setHeight(newHeight);
        setIsExpanded(newHeight > 40);
        didDragRef.current = true;
        lastDragY.current = e.clientY;
        lastDragHeight.current = newHeight;
      }
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (didDragRef.current) {
      const wasOpen = dragStartHeight.current > 40;
      const isOpen = lastDragHeight.current > 40;
      if (!wasOpen && isOpen) barEvents.opened("drag");
      if (wasOpen && !isOpen) barEvents.closed("drag");
    }
    // Add a small delay before removing drag active state to prevent flickering
    setTimeout(() => {
      setIsDragActive(false);
      didDragRef.current = false;
    }, 100);
  }, [barEvents]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const toggleExpand = (half: AuditBarTrigger, openHeight = 200) => {
    setIsPeek(false);
    if (isExpanded) {
      setHeight(40);
      setIsExpanded(false);
      barEvents.closed(half);
    } else {
      setHeight(openHeight);
      setIsExpanded(true);
      barEvents.opened(half);
    }
  };

  const handleToggleExpand = (half: AuditBarTrigger) => {
    if (didDragRef.current) return;
    toggleExpand(half);
  };

  const handleDoubleClick = (half: AuditBarTrigger) => {
    if (didDragRef.current) return;
    toggleExpand(half, 300);
  };

  const notesTextarea = (
    <Textarea
      ref={textareaRef}
      value={notes}
      onChange={(e) => setNotes(e.target.value)}
      placeholder="Add your notes, questions, or reasoning here... These notes will be visible to coding admins."
      className="h-full resize-none border-gray-300 focus:border-blue-500 focus:ring-blue-500"
      aria-label="Review notes"
    />
  );

  return (
    <div
      ref={containerRef}
      className={`flex-shrink-0 bg-white border-t border-gray-300 shadow-lg z-20 ${
        isDragging ? "transition-none" : "transition-all duration-200 ease-out"
      } ${isDragActive ? "shadow-2xl" : ""}`}
      style={{ height: `${height}px` }}
    >
      {/* Drag Handle */}
      {(() => {
        const handle = (
          <div
            className={`flex items-center justify-center min-h-[38px] cursor-ns-resize transition-colors ${
              audit ? "flex-1" : ""
            } ${isDragging ? "bg-blue-50 border-b-2 border-blue-300" : "hover:bg-gray-100"}`}
            onMouseDown={handleMouseDown}
            onClick={() => handleToggleExpand("notes-half")}
            onDoubleClick={() => handleDoubleClick("notes-half")}
            role="button"
            tabIndex={0}
            aria-label="Drag to resize notes panel, double-click to toggle size"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                toggleExpand("notes-half");
              }
            }}
          >
            <GripHorizontal className={`h-5 w-5 ${isDragging ? "text-blue-500" : "text-gray-400"}`} />
            <span className={`ml-2 text-sm ${isDragging ? "text-blue-600 font-medium" : "text-gray-500"}`}>
              Notes
              {isSaving && <span className="ml-2 text-xs text-blue-600">Saving...</span>}
            </span>
          </div>
        );
        if (!audit) return handle;
        return (
          <div className="flex min-h-[38px]">
            {handle}
            <AuditButton
              audit={audit}
              onMouseDown={handleMouseDown}
              onClick={() => handleToggleExpand("audit-half")}
              onDoubleClick={() => handleDoubleClick("audit-half")}
            />
          </div>
        );
      })()}

      {/* Notes Content */}
      {isExpanded && !audit && <div className="px-4 pb-4 h-[calc(100%-38px)] overflow-hidden">{notesTextarea}</div>}
      {isExpanded && audit && (
        <div className="flex gap-4 px-4 pb-4 h-[calc(100%-38px)] overflow-hidden">
          <div className="flex-1 min-w-0">{notesTextarea}</div>
          <AuditPane audit={audit} />
        </div>
      )}
    </div>
  );
};

// Memoize to prevent re-renders when onSave reference changes
// We use a ref internally to access the latest onSave function
export const ReviewNotes = memo(ReviewNotesComponent, (prevProps, nextProps) => {
  // Only re-render if caseId, initialNotes or audit change
  // Ignore onSave changes since we use a ref internally
  return (
    prevProps.caseId === nextProps.caseId &&
    prevProps.initialNotes === nextProps.initialNotes &&
    prevProps.audit === nextProps.audit
  );
});
